import * as React from "react";
import {isEqual, groupBy} from 'lodash';
import {Button, Checkbox, CircularProgress, List, ListItem, ListItemText, ListSubheader, Tooltip} from "@mui/material";
import {
  isForeignWordNode,
  isNumeralNode,
  isWordNode,
  StructureNode,
  WordNode
} from "languagetool-player/src/models/Node";
import {getWordTypeName, WordNodeOption} from "./WordNodeOption";
import {isQueryByWordId, WordId} from "languagetool-player/src/annotations";
import {
  AnalyzeResult,
  AutoAssignDecisionCreateDraft,
  AutoAssignDecisionCustomDraft,
  AutoAssignDecisionEditDraft,
  isForeignWordNodeResult,
  isNumeralNodeResult,
  isWordNodeResult
} from "../wordsApi";
import type {AIDecisionNote} from "./index";
import {getCanonicalFromwWordId} from "languagetool-player/src/ui/WordPopup/parts/WordDisplay";
import { Fragment } from "react";
import {useQuery} from "multi-apollo";
import {gql as gqlw} from "@apollo/client";
import {NumeralNodeOption} from "./NumeralNodeOption";
import {ForeignWordNodeOption} from "./ForeignWordNodeOption";
import {useDialogs} from "../../../ui/dialogs/DialogManager";
import {CustomWordDialogResult, CustomWordNodeDialog} from "./CustomWordNodeDialog";
import {FarsiVerb, FarsiWordNode} from "languagetool-player/src/langpacks/fa";
import {OptionPickerWordDataQuery} from "../../../../types/OptionPickerWordDataQuery";
import {Vocable, VocableId} from "languagetool-player/src/models/formats/CaptionTrack";
import styles from "./NodeOptionPicker.module.css";


type AIDraft = AutoAssignDecisionCreateDraft|AutoAssignDecisionEditDraft|AutoAssignDecisionCustomDraft;

type Props = {
  // The set of vocables for which we want to pick a word node to assign.
  vocables: Vocable[],
  // These are suggestions by the server for what nodes match the vocable text here.
  suggestedNodes?: AnalyzeResult[],
  // This is the current word node assigned to the vocable(s) in question.
  currentNode: StructureNode|null,

  language: string,
  vocableText: string,
  extraButtons?: any,
  showJSON?: boolean,
  aiDraft?: AIDraft,
  aiDecisionNote?: AIDecisionNote,

  onCreateWordClick: (word: string, vocableIds: VocableId[]) => void,
  onEditWordClick: (wordId: number, vocableText: string) => void,
  onOptionSelected: (vocableIds: VocableId[], option: AnalyzeResult|null) => void,
  onAssignCustomNode: (vocableIds: VocableId[], node: WordNode) => void,
  onApplyAIDraft: (draft: AIDraft) => void,
  onDismissAIDraft: (vocableIds: VocableId[]) => void,
}


function isSameWordId(a: WordId, b: WordId) {
  if (isQueryByWordId(a) && isQueryByWordId(b)) {
    return a.id === b.id;
  }
  return isEqual(a, b);
}

function AIDraftPanel(props: {
  draft: AIDraft,
  onApply: () => void,
  onDismiss: () => void,
}) {
  const label = props.draft.action === 'create_word_draft'
    ? 'AI draft: create word'
    : props.draft.action === 'edit_word_draft'
      ? 'AI draft: edit word'
      : 'AI draft: custom node';

  return <div style={{
    marginLeft: '17px',
    marginBottom: '15px',
    maxWidth: '500px',
    backgroundColor: '#fff8e1',
    padding: '10px',
    borderLeft: '4px solid #ffb300'
  }}>
    <strong>{label}</strong>
    {props.draft.reason ? <div style={{marginTop: '6px'}}>{props.draft.reason}</div> : null}
    {props.draft.confidence ? <div style={{marginTop: '6px', fontSize: '12px', color: '#6d4c41'}}>Confidence: {props.draft.confidence}</div> : null}
    <div style={{marginTop: '10px', display: 'flex', gap: '8px'}}>
      <Button size={'small'} variant={'contained'} onClick={props.onApply}>Open</Button>
      <Button size={'small'} onClick={props.onDismiss}>Dismiss</Button>
    </div>
  </div>
}

function AISkipPanel(props: {note: AIDecisionNote}) {
  return <div style={{
    marginLeft: '17px',
    marginBottom: '15px',
    maxWidth: '500px',
    backgroundColor: '#eceff1',
    padding: '10px',
    borderLeft: '4px solid #78909c'
  }}>
    <strong>AI skipped this slot</strong>
    <div style={{marginTop: '6px'}}>{props.note.reason || 'No confident suggestion.'}</div>
    {props.note.confidence ? <div style={{marginTop: '6px', fontSize: '12px', color: '#455a64'}}>Confidence: {props.note.confidence}</div> : null}
  </div>
}

function AIErrorPanel(props: {note: AIDecisionNote}) {
  return <div style={{
    marginLeft: '17px',
    marginBottom: '15px',
    maxWidth: '500px',
    backgroundColor: '#ffebee',
    padding: '10px',
    borderLeft: '4px solid #e53935'
  }}>
    <strong>AI could not produce a valid suggestion</strong>
    <div style={{marginTop: '6px'}}>{props.note.reason || 'AI validation failed repeatedly.'}</div>
    {props.note.confidence ? <div style={{marginTop: '6px', fontSize: '12px', color: '#b71c1c'}}>Confidence: {props.note.confidence}</div> : null}
  </div>
}

/**
 * Shows a list of word node options suggested by the server.
 *
 * Let the user pick one of the given word nodes by checking it.
 */
export const NodeOptionPicker = React.memo((props: Props) => {
  const {currentNode} = props;
  let {suggestedNodes} = props;

  const dialogs = useDialogs();

  const showCustomNodeDialog = React.useCallback(() => {
    dialogs.show(CustomWordNodeDialog, {
      subtitleText: props.vocableText,
      onOk: (data: CustomWordDialogResult) => {
        props.onAssignCustomNode(props.vocables.map(v => v.id), {
          id: '',
          vocables: props.vocables.map(v => v.id),
          type: 'word',
          wordId: {id: data.wordId},
          isManual: {
            diacritization: data.diacritization
          }
        })
      }
    });
  }, [props.vocableText, props.onAssignCustomNode, props.vocables])

  // We started to move those behind the "create word" dialog
  const hideWordListNodes = true;
  if (hideWordListNodes && suggestedNodes) {
    suggestedNodes = suggestedNodes.filter(result => !isWordNodeResult(result) || isQueryByWordId(result.node.wordId));
  }

  type InlineNode = {node: StructureNode, unlinkedNodeResult?: boolean};

  // This is a bit complicated, but we really have to do this, and here.
  // We have to merge the node suggestions from the server with the nodes attached
  // in the unit itself, such that if the attached node is one of the server nodes,
  // we collapse them, but if the attached node is no longer returned from the server,
  // we still show it separately.
  let selectedResult: InlineNode|undefined|null = (currentNode && suggestedNodes) ? suggestedNodes.find(result => {
    if (isNumeralNodeResult(result)) {
      if (!isNumeralNode(currentNode)) {
        return false;
      }
      const {node: newNode} = result;
      return (
        newNode.quantity === currentNode.quantity &&
        newNode.isOrdinal === currentNode.isOrdinal &&
        newNode.type === currentNode.type
      )
    }

    if (isForeignWordNodeResult(result)) {
      return true;
    }

    if (!isWordNode(currentNode)) {
      return false;
    }

    // Always keep the manual node.
    if (result.node.isManual) {
      return true;
    }

    const node = result.node;
    // noinspection RedundantIfStatementJS
    if (
      isSameWordId(node.wordId, currentNode.wordId) &&
      isEqual(node.inflection, currentNode.inflection) &&
      isEqual(node.structure, currentNode.structure)
    ) {
      return true;
    }
    return false;
  }) : null;

  const allResults: AnalyzeResult[] = [...(suggestedNodes || [])];

  // If there is a node assigned, and we could not match that node to a server result, then insert that node
  // into the server result set, and mark it has "no longer matching".
  if (!selectedResult && currentNode) {
    let result = {
      node: currentNode,
      unlinkedNodeResult: (isWordNode(currentNode) && currentNode.isManual) ? false : true
    };
    // @ts-ignore: InlineResult and AnlayzeResult are just not compatible
    allResults.push(result);
    selectedResult = result;
  };

  if (!allResults || !allResults.length) {
    // This basically means that either our logic to "deduce" the base form of a word is a problem, because
    // it could not deal with this (and needs fixing), or, more likely, this is a word which is not in our
    // database; as our algorithm relies on a word database to gain confidence which of many possible
    // combinations of morphenes is the right one.
    // For editing purposes, it only makes sense to assume a missing word, so we provide the user with the
    // ability to add one.
    return <div>
      {props.aiDraft ? <AIDraftPanel
        draft={props.aiDraft}
        onApply={() => props.onApplyAIDraft(props.aiDraft!)}
        onDismiss={() => props.onDismissAIDraft(props.vocables.map(v => v.id))}
      /> : null}
      {props.aiDecisionNote?.action === 'skip' ? <AISkipPanel note={props.aiDecisionNote} /> : null}
      {props.aiDecisionNote?.action === 'error' ? <AIErrorPanel note={props.aiDecisionNote} /> : null}

      <div><em>This word is not known.</em></div>

      <Button
        variant="contained"
        color="primary"
        size={"small"}
        // So we pass along any suggested nodes from the server here
        onClick={() => props.onCreateWordClick(props.vocableText, props.vocables.map(v => v.id))}
      >
        Create this word
      </Button>

      <Button
        variant="contained"
        size={"small"}
        onClick={() => showCustomNodeDialog()}
      >
        Custom Node
      </Button>

      {props.extraButtons}
    </div>
  }

  // For clarity, we want to group the results by word id
  const groupedByWord = Object.values(groupBy(allResults, 'node.wordId.id'));
  const hasMultipleGroups = groupedByWord.length > 1;

  return <>
    {props.aiDraft ? <AIDraftPanel
      draft={props.aiDraft}
      onApply={() => props.onApplyAIDraft(props.aiDraft!)}
      onDismiss={() => props.onDismissAIDraft(props.vocables.map(v => v.id))}
    /> : null}
    {props.aiDecisionNote?.action === 'skip' ? <AISkipPanel note={props.aiDecisionNote} /> : null}
    {props.aiDecisionNote?.action === 'error' ? <AIErrorPanel note={props.aiDecisionNote} /> : null}

  <List disablePadding>
    {groupedByWord.map((results, idx) => {
      const isFirst = idx == 0;
      const firstResult = results[0];
      const wordId = isWordNode(firstResult.node) ? firstResult.node.wordId : undefined;

      const editButton = isWordNodeResult(firstResult) && (isQueryByWordId(wordId) && (!firstResult.wordData?.isReviewed))
        ? <Button
            key={'editButton'}
            size={"small"}
            style={{
              marginLeft: '30px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              props.onEditWordClick(wordId.id, props.vocableText);
            }}
          >
            Edit word
          </Button>
        :
          null;

      return <Fragment key={idx}>

        {isFirst
          ?
            <div style={{
              position: 'absolute',
              zIndex: 50,
              right: 0
            }}>
              <Button
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  showCustomNodeDialog();
                }}
              >
                Custom
              </Button>

              <Button
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onCreateWordClick(props.vocableText, props.vocables.map(v => v.id))
                }}
              >
                Create New Word
              </Button>

              {props.extraButtons}
            </div>
          : null}

        {(hasMultipleGroups || isWordNodeResult(results[0]))
          ? <OptionGroupHeader
              result={results[0]}
              editButton={editButton}
            />
          : null}

        {(isWordNodeResult(results[0]) && results[0].wordData?.editorConfig?.warning && results.indexOf(selectedResult as any) > -1) ? <div  style={{
          marginLeft: '17px',
          marginBottom: '15px',
          maxWidth: '500px',
          backgroundColor: '#ffcdd2',
          padding: '10px'
        }}>
          <strong><span style={{color: '#f44336'}}>IMPORTANT. Please double check this entry.</span> The entry you selected
            has additional instructions:</strong>
          <div style={{marginTop: '10px'}}>
            {results[0].wordData?.editorConfig?.warning}
          </div>
        </div> : null}

        {
          results.map((result, jdx) => {
            const isSelected = result === selectedResult;
            return <SingleItem
              hasIndent={hasMultipleGroups}
              result={result}
              key={`${idx}-${jdx}`}
              isSelected={isSelected}
              showJSON={props.showJSON}
              {...props}
            />
          })
        }
      </Fragment>
    })}
  </List>
  </>
});


export type WordDataQueryResult = {
  base?: string,
  description: string,
  isReviewed: boolean,
  sounds: string[]
};

function useQueryWordData(wordId: number|undefined): WordDataQueryResult|null {
  const {data, error, loading, refetch} = useQuery<OptionPickerWordDataQuery>(gqlw`
    query OptionPickerWordDataQuery($wordId: Int!) {
      word(id: $wordId) {
        base,
        description,
        isReviewed,
        baseTransliteration
      }
    }
  `, {
    client: 'words',
    skip: !wordId,
    variables: {
      wordId
    }
  });

  return data?.word ? {
    base: data.word.base || "",
    description: data.word.description || "",
    isReviewed: data.word.isReviewed || false,
    sounds: JSON.parse(data.word.baseTransliteration || "[]")
  } : null;
}


function useWordData(result: AnalyzeResult): WordDataQueryResult|undefined {
  // Mostly, further information about the word is attached to the server result
  const wordDataFromNode = isWordNodeResult(result) ? result?.wordData : null;

  // Sometimes the data comes from storage, and we do not have the data
  const wordDataFromQuery = useQueryWordData(
    (isWordNodeResult(result) && !wordDataFromNode)
      ? result.node.wordId.id
      : undefined
  );

  return wordDataFromQuery || wordDataFromNode || undefined;
}

type WordLookupMeaningDefinition = {
  words?: string[]|null,
  text?: string|null,
};

type WordLookupMeaning = {
  id: string|number,
  descriptor?: string|null,
  definitions?: WordLookupMeaningDefinition[]|null,
};

type WordLookupWordQueryData = {
  word?: {
    id: number,
    base: string,
    meanings?: WordLookupMeaning[]|null,
  }|null,
};

type WordLookupLineVocable = {
  id?: string,
  text?: string,
  pre?: string,
  post?: string,
};

type WordLookupCorpusOccurrence = {
  lineIndex: number,
  lineText?: string|null,
  lineStructure?: string|null,
  node?: string|null,
  translations?: {text?: string|null}[]|null,
};

type WordLookupCorpusQueryData = {
  wordInfo?: {
    wordId: number,
    uses?: {
      edges?: {
        node?: {
          unit?: {
            id: string,
            name: string,
          }|null,
          occurrences?: WordLookupCorpusOccurrence[]|null,
        }|null,
      }[]|null,
    }|null,
  }[]|null,
};

type LookupMeaningItem = {
  text: string,
  descriptor?: string,
  note?: string,
};

type LookupExampleItem = {
  sentence: string,
  translation?: string,
  unitName?: string,
  lineIndex: number,
  vocables?: WordLookupLineVocable[],
  matchedVocableIds?: string[],
};

function normalizeLookupText(text?: string[]|string|null): string[] {
  if (!text) {
    return [];
  }
  if (Array.isArray(text)) {
    return text.map(item => item.trim()).filter(Boolean);
  }
  return text.split(',').map(item => item.trim()).filter(Boolean);
}

function dedupeLookupItems<T>(items: T[], getKey: (item: T) => string) {
  const seen = new Set<string>();
  return items.filter(item => {
    const key = getKey(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function safeParseJson<T>(rawData?: string|null): T|null {
  if (!rawData) {
    return null;
  }
  try {
    return JSON.parse(rawData) as T;
  }
  catch {
    return null;
  }
}

function idsEqual(a?: string|number|null, b?: string|number|null) {
  return a != null && b != null && `${a}` === `${b}`;
}

function buildLookupExampleItem(
  occurrence: WordLookupCorpusOccurrence,
  unitName?: string,
  selectedMeaningId?: number,
): LookupExampleItem|null {
  const node = safeParseJson<any>(occurrence.node);
  const nodeMeaningId = node?.wordId?.meaningId;

  if (selectedMeaningId && nodeMeaningId && !idsEqual(nodeMeaningId, selectedMeaningId)) {
    return null;
  }

  const lineStructure = safeParseJson<WordLookupLineVocable[]>(occurrence.lineStructure);
  const vocables = Array.isArray(lineStructure)
    ? lineStructure.filter((item): item is WordLookupLineVocable => !!item && typeof item.text === 'string')
    : [];

  const sentence = (
    vocables.length
      ? vocables.map(item => `${item.pre || ''}${item.text || ''}${item.post || ''}`).join('')
      : occurrence.lineText || ''
  ).trim();

  if (!sentence) {
    return null;
  }

  const translation = occurrence.translations
    ?.map(item => item?.text?.trim())
    .find((text): text is string => !!text);

  return {
    sentence,
    translation,
    unitName,
    lineIndex: occurrence.lineIndex,
    vocables: vocables.length ? vocables : undefined,
    matchedVocableIds: Array.isArray(node?.vocables) ? node.vocables : undefined,
  };
}

function useWordLookupData(wordQuery: {id: number, meaningId?: number}|undefined, enabled: boolean) {
  const {data: wordData, loading: wordLoading} = useQuery<WordLookupWordQueryData>(gqlw`
    query WordLookupTooltipWordQuery($wordId: Int!) {
      word(id: $wordId) {
        id
        base
        meanings {
          id
          descriptor
          definitions {
            text
            words
          }
        }
      }
    }
  `, {
    client: 'words',
    skip: !wordQuery?.id || !enabled,
    variables: {
      wordId: wordQuery?.id,
    }
  });

  const {data: corpusData, loading: corpusLoading} = useQuery<WordLookupCorpusQueryData>(gqlw`
    query WordLookupTooltipCorpusQuery($ids: [Int!]!) {
      wordInfo(ids: $ids) {
        wordId
        uses(max: 3) {
          edges {
            node {
              unit {
                id
                name
              }
              occurrences(includeTranslations: ["en"], max: 2) {
                lineIndex
                lineText
                lineStructure
                node
                translations {
                  text
                }
              }
            }
          }
        }
      }
    }
  `, {
    client: 'market',
    skip: !wordQuery?.id || !enabled,
    variables: {
      ids: wordQuery ? [wordQuery.id] : [],
    }
  });

  return React.useMemo(() => {
    const selectedMeaningId = wordQuery?.meaningId;
    const allMeanings = wordData?.word?.meanings || [];
    const selectedMeanings = selectedMeaningId
      ? allMeanings.filter(meaning => idsEqual(meaning.id, selectedMeaningId))
      : allMeanings;
    const meaningsToRender = selectedMeanings.length ? selectedMeanings : allMeanings;

    const meanings: LookupMeaningItem[] = [];
    meaningsToRender.forEach((meaning, meaningIdx) => {
      (meaning.definitions || []).forEach((definition) => {
        const words = normalizeLookupText(definition?.words || []);
        const note = definition?.text?.trim() || undefined;
        const text = words.join(', ') || note;
        if (!text) {
          return;
        }
        meanings.push({
          text,
          descriptor: meaning?.descriptor?.trim() || ((meaningsToRender.length || 0) > 1 ? `Meaning ${meaningIdx + 1}` : undefined),
          note: words.length ? note : undefined,
        });
      });
    });

    const examples: LookupExampleItem[] = [];
    (corpusData?.wordInfo?.[0]?.uses?.edges || []).forEach((edge) => {
      const useNode = edge?.node;
      const unitName = useNode?.unit?.name || undefined;
      (useNode?.occurrences || []).forEach((occurrence) => {
        const example = buildLookupExampleItem(occurrence, unitName, selectedMeaningId);
        if (example) {
          examples.push(example);
        }
      });
    });

    return {
      loading: enabled && (wordLoading || corpusLoading),
      word: wordData?.word || null,
      meanings: dedupeLookupItems(
        meanings,
        item => `${item.descriptor || ''}|${item.text.toLowerCase()}|${item.note || ''}`,
      ).slice(0, 6),
      examples: dedupeLookupItems(
        examples,
        item => `${item.sentence.toLowerCase()}|${item.translation || ''}|${item.unitName || ''}`,
      ).slice(0, 3),
    };
  }, [corpusData, corpusLoading, enabled, wordData, wordLoading, wordQuery?.meaningId]);
}

function WordLookupTooltipCard(props: {
  wordLabel: string,
  wordType?: string,
  description?: string,
  loading?: boolean,
  meanings: LookupMeaningItem[],
  examples: LookupExampleItem[],
}) {
  return <div className={styles.lookupTooltip}>
    <div className={styles.lookupTooltipHeader}>
      <div>
        <div className={styles.lookupTooltipWord}>{props.wordLabel}</div>
        {props.description ? <div className={styles.lookupTooltipDescription}>{props.description}</div> : null}
      </div>
      {props.wordType ? <div className={styles.lookupTooltipType}>{props.wordType}</div> : null}
    </div>

    {props.loading ? <div className={styles.lookupTooltipLoading}>
      <CircularProgress size={16} sx={{color: '#2563eb'}} />
      <div>Loading internal meanings and unit examples…</div>
    </div> : null}

    {!props.loading ? <>
      <div className={styles.lookupTooltipSection}>
        <div className={styles.lookupTooltipSectionTitle}>Meanings</div>
        {props.meanings.length
          ? <div className={styles.lookupTooltipList}>
              {props.meanings.map((item, idx) => <div key={idx} className={styles.lookupCard}>
                {item.descriptor ? <div className={styles.lookupDescriptor}>{item.descriptor}</div> : null}
                <div className={styles.lookupPrimary}>{item.text}</div>
                {item.note ? <div className={styles.lookupSecondary}>{item.note}</div> : null}
              </div>)}
            </div>
          : <div className={styles.lookupEmpty}>No internal meaning notes are available yet.</div>}
      </div>

      <div className={styles.lookupTooltipSection}>
        <div className={styles.lookupTooltipSectionTitle}>From our units</div>
        {props.examples.length
          ? <div className={styles.lookupTooltipList}>
              {props.examples.map((item, idx) => <div key={`${item.unitName || 'unit'}-${item.lineIndex}-${idx}`} className={styles.lookupCard}>
                <div className={styles.lookupSentence} dir="rtl">
                  {item.vocables?.length
                    ? item.vocables.map((vocable, vocableIdx) => {
                        const isMatch = !!(vocable.id && item.matchedVocableIds?.includes(vocable.id));
                        return <span key={`${vocable.id || 'vocable'}-${vocableIdx}`}>
                          {vocable.pre || ''}
                          <span className={isMatch ? styles.lookupSentenceMatch : undefined}>{vocable.text}</span>
                          {vocable.post || ''}
                        </span>;
                      })
                    : item.sentence}
                </div>
                {item.translation ? <div className={styles.lookupSecondary} dir="ltr">{item.translation}</div> : null}
                <div className={styles.lookupMeta}>
                  {item.unitName ? <span>{item.unitName}</span> : null}
                  <span>Line {item.lineIndex + 1}</span>
                </div>
              </div>)}
            </div>
          : <div className={styles.lookupEmpty}>No unit examples reference this word yet.</div>}
      </div>
    </> : null}
  </div>;
}

/**
 * Multiple Grammar Results are grouped by word id, this is the header describing the word in the group.
 */
function OptionGroupHeader(props: {result: AnalyzeResult, editButton?: any}) {
  const {result} = props;

  const wordData = useWordData(result);
  const [hasOpenedLookup, setHasOpenedLookup] = React.useState(false);
  const wordResult = isWordNodeResult(result) ? result : null;
  const wordNode = wordResult?.node;
  const lookupWordQuery = (wordNode && isQueryByWordId(wordNode.wordId)) ? wordNode.wordId : undefined;
  const lookup = useWordLookupData(lookupWordQuery, hasOpenedLookup);

  let content;
  if (wordResult && wordNode) {
    const isReviewed = wordData?.isReviewed;
    const wordType = wordNode.inflection ? getWordTypeName((wordNode.inflection as FarsiVerb).type) : "unknown";
    const wordLabel = (wordData as WordDataQueryResult)?.base || getCanonicalFromwWordId(wordNode.wordId);

    const headerText = <>
      <span style={{
        textTransform: 'uppercase',
        fontSize: '.8em'
      }}>
        {wordType}
      </span>: {wordLabel}
      {
        (wordData?.description)
          ? <em style={{}}> - {wordData.description}</em>
          : null
      }
      {isReviewed ? <CheckIcon style={{paddingLeft: '5px'}} /> : null}
    </>;

    content = lookupWordQuery
      ? <Tooltip
          disableInteractive={false}
          placement="left-start"
          enterDelay={150}
          title={<WordLookupTooltipCard
            wordLabel={lookup.word?.base || wordLabel}
            wordType={wordType}
            description={wordData?.description}
            loading={lookup.loading}
            meanings={lookup.meanings}
            examples={lookup.examples}
          />}
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'transparent',
                padding: 0,
                maxWidth: 'none'
              }
            }
          }}
          onOpen={() => setHasOpenedLookup(true)}
        >
          <span tabIndex={0} className={`${styles.lookupLabel} ${styles.lookupTrigger}`}>
            {headerText}
          </span>
        </Tooltip>
      : <span className={styles.lookupLabel}>
          {headerText}
        </span>;
  }
  else {
    content = <span className={styles.lookupLabel}>
      Number
    </span>
  }

  return <ListSubheader style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'}}>
    <span>{content}</span>
    {props.editButton}
  </ListSubheader>
}


export function SingleItem(props: {
  result: AnalyzeResult,
  isSelected: boolean,

  onOptionSelected: (vocableIds: VocableId[], option: AnalyzeResult|null) => void,
  // It might be better to keep this further up.
  language: string,
  vocables: Vocable[],

  hasIndent?: boolean,
  showJSON?: boolean
}) {
  const {isSelected, result} = props;

  const wordData = useWordData(result);

  const wordId = isWordNode(result.node) ? result.node.wordId : undefined;

  let style = {
    color: (isWordNode(result.node) && !isQueryByWordId(wordId)) ? '#616060' : undefined,
  };

  let display;
  if (isWordNode(result.node)) {
    display = <WordNodeOption
      wordNode={result.node as FarsiWordNode}
      wordData={wordData}
      language={props.language}
      showReviewedMark={!props.hasIndent}
    />
  }
  else if (isForeignWordNode(result.node)) {
    display = <ForeignWordNodeOption
      node={result.node}
    />;
  }
  else if (isNumeralNode(result.node)) {
    display = <NumeralNodeOption
      node={result.node}
    />;
  }
  else {
    display = <div>Not supported</div>
  }

  return <ListItem
    dense
    button
    data-wordid={wordId ? wordId.id : null}
    onClick={() => {
      if (isSelected) {
        props.onOptionSelected(props.vocables.map(v => v.id), null);
      } else {
        props.onOptionSelected(props.vocables.map(v => v.id), result);
      }
    }}
    style={{
      backgroundColor: result.unlinkedNodeResult ? '#ffcdd2' : undefined,
      marginLeft: props.hasIndent ? '30px' : undefined,
      // Without this, marginLeft sometimes causes a scrollbar to appear because the default is width: 100%. Annoyingly,
      // not always, and not sure if there is a better fix.
      width: 'auto'
    }}
  >
    <Checkbox
      checked={isSelected}
      tabIndex={-1}
      disableRipple
      style={{padding: 0}}
    />
    <ListItemText
      disableTypography={true}
      style={style}
      primary={
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <div style={{flex: 1}}>
            {display}
            {props.showJSON ? <pre style={{userSelect: 'text'}} onMouseDown={(e) => { e.stopPropagation(); }} onClick={(e) => { e.stopPropagation(); }}>
              {JSON.stringify(result.node, null, 4)}</pre> : null}
          </div>
        </div>
      }
    />
  </ListItem>
}


export const  CheckIcon = (props: any) => (
  <svg width={"1em"} height={"1em"} viewBox="0 0 512 512" {...props}>
    <path
      fill="#32BEA6"
      d="M504.1 256C504.1 119 393 7.9 256 7.9S7.9 119 7.9 256 119 504.1 256 504.1 504.1 393 504.1 256z"
    />
    <path
      fill="#FFF"
      d="M392.6 172.9c-5.8-15.1-17.7-12.7-30.6-10.1-7.7 1.6-42 11.6-96.1 68.8-22.5 23.7-37.3 42.6-47.1 57-6-7.3-12.8-15.2-20-22.3-22.1-22.1-46.8-37.3-47.8-37.9-10.3-6.3-23.8-3.1-30.2 7.3-6.3 10.3-3.1 23.8 7.2 30.2.2.1 21.4 13.2 39.6 31.5 18.6 18.6 35.5 43.8 35.7 44.1 4.1 6.2 11 9.8 18.3 9.8 1.2 0 2.5-.1 3.8-.3 8.6-1.5 15.4-7.9 17.5-16.3.1-.2 8.8-24.3 54.7-72.7 37-39.1 61.7-51.5 70.3-54.9h.3s.3-.1.8-.4c1.5-.6 2.3-.8 2.3-.8-.4.1-.6.1-.6.1v-.1c4-1.7 11.4-4.9 11.5-5 11.1-4.8 14.8-16.8 10.4-28z"
    />
  </svg>
)